import * as O from 'fp-ts/Option';
import { ModaSofa } from '@dora/backpack';
import { pipe } from 'fp-ts/function';
import { Defaults } from './defaults';
import { SeatSize } from './seatSizes';
import { numberOfSeatsFromSize } from './numberOfSeatsFromSize';

/**
 * Construct the initial Modular Sofa form state based on a possible existing sofa and some options
 * based on the sofa we're configuring.
 *
 * If there is no curently selected sofa, then fall back to the defaults provided
 */
export function initialSofaState(
    current: O.Option<ModaSofa.Configuration>,
    seatSizes: SeatSize,
    defaults: Defaults,
) {
    const getNumberOfSeats = numberOfSeatsFromSize(
        seatSizes.endPieceLeft,
        seatSizes.endPieceRight,
        seatSizes.middlePiece,
    )(defaults);

    const defaultShape = pipe(
        current,
        O.fold(
            () => defaults.shape,
            c => c.size.shape,
        ),
    );

    const defaultSizes = pipe(
        current,
        O.map(c => c.size),
        O.fold(
            () => getNumberOfSeats({}, defaultShape),
            x => getNumberOfSeats(x, defaultShape),
        ),
    );

    const defaultCornerStyle = pipe(
        current,
        O.map(c => c.cornerStyle),
        O.getOrElse((): ModaSofa.CornerStyleT => ModaSofa.DefaultCornerStyle),
    );

    const currentPlacements = pipe(
        current,
        O.chain(c => O.fromNullable(c.interstitialPlacements['drinks-cooler-armrest'])),
        O.map(x => x.placements),
        O.getOrElse(() => [] as number[]),
    );

    const currentSize = pipe(
        current,
        O.map(x => x.size),
        O.toNullable,
    );

    // Our UI currently only cares about how many interstitial pieces each sofa arm has -
    // so we'll convert the interstitial placement data into this simpler format.
    let defaultArms = { left: 0, middle: 0, right: 0 };
    if (currentPlacements.length > 0) {
        const isLeftArm = (index: number) =>
            currentSize !== null &&
            (currentSize.shape !== 'straight' ? index <= currentSize.left : false);

        const isMiddleArm = (index: number) =>
            currentSize !== null &&
            (currentSize.shape === 'straight'
                ? true
                : currentSize.shape === 'u-shape'
                ? index <= currentSize.left + currentSize.middle + 1 &&
                  index >= currentSize.left + 1
                : false);

        const isRightArm = (index: number) =>
            currentSize !== null &&
            (currentSize.shape === 'corner'
                ? index > currentSize.left
                : currentSize.shape === 'u-shape'
                ? index > currentSize.left + 1 + currentSize.middle
                : false);

        defaultArms = currentPlacements.reduce(
            (m, x) => ({
                left: m.left + (isLeftArm(x) ? 1 : 0),
                right: m.right + (isRightArm(x) ? 1 : 0),
                middle: m.middle + (isMiddleArm(x) ? 1 : 0),
            }),
            defaultArms,
        );
    }

    return { defaultArms, defaultCornerStyle, defaultShape, defaultSizes };
}
